import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import {
  Auth0ClientService,
  AuthModule,
  AuthService,
  PopupCancelledError,
} from '@auth0/auth0-angular';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { SocialOffersService } from '../services/social-offers.service';
import { SocialOffersListComponent } from '../social-offers-list/social-offers-list.component';
import { BANNER_CONFIG } from '../data/banner.data';
import { couponUser, GUEST_USER } from '../data/user.data';
import { AuthenticateService } from '../services/authenticate.service';
import { CouponsService } from '../services/coupons.service';
import { DomianProviderService } from '../services/domain-provider.service';
import { envs } from '../data/env.data';
import { getBannerFromUrl } from '../services/appconfig.service';

@Component({
  selector: 'app-banner',
  standalone: true,
  imports: [CommonModule, AuthModule, RouterModule, SocialOffersListComponent],
  providers: [
    AuthService,
    AuthenticateService,
    SocialOffersService,
    CouponsService,
  ],
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerComponent implements OnInit, OnDestroy {
  readonly isInIframe = computed(() => window.self !== window.top);
  private readonly auth = inject(Auth0ClientService);
  readonly couponAuth = inject(AuthenticateService);
  private readonly bannerService = inject(DomianProviderService);
  private readonly route = inject(ActivatedRoute);

  private readonly destroy$ = new Subject<void>();

  readonly logoSrc = signal<string>('');
  readonly isLoading = signal<boolean>(false);
  readonly user = signal<any>(null);

  readonly banner = computed(() => {
    return envs.useParamsForBanner
      ? getBannerFromUrl()
      : this.bannerService.getBanner();
  });

  readonly userName = computed(() => this.user()?.given_name || GUEST_USER);
  readonly getLoginText = computed(() =>
    this.user() ? 'Sign out' : 'Sign In or Register'
  );

  async ngOnInit(): Promise<void> {
    this.initializeBanner();
    const loginConfig = this.buildLoginConfig(true);
    if (this.isInIframe()) {
      await this.loginInFrame(loginConfig);
      return;
    }
    try {
      const token = await this.auth.getTokenSilently({
        authorizationParams: loginConfig.authorizationParams,
        cacheMode: 'on', // Use cached token if available
      });
    } catch (error: any) {
      // Silent auth failed - user needs to login
      if (
        error.error === 'login_required' ||
        error.error === 'consent_required'
      ) {
        console.warn(
          'silent authentication failed - user interaction required:',
          error
        );
      } else if (error.error === 'missing_refresh_token') {
        console.warn('Refresh token missing - will need re-authentication');
      } else {
        console.error('Silent authentication error:', error);
      }
    } finally {
      this.setupAuthSubscription();
    }
  }
  async loginInFrame(loginConfig: any): Promise<void> {
    try {
      const token = await this.auth.getTokenSilently({
        authorizationParams: loginConfig.authorizationParams,
        cacheMode: 'on', // Use cached token if available
      });
    } catch (error: any) {
      // Silent auth failed - user needs to login
      if (
        error.error === 'login_required' ||
        error.error === 'consent_required'
      ) {
        console.warn(
          'silent authentication failed - user interaction required:',
          error
        );
        if (this.isInIframe()) {
          try {
            await this.auth.loginWithPopup(loginConfig);
          } catch (e) {
            console.log('Silent authentication failed in popup:', e);
            if (e instanceof PopupCancelledError) {
              // Popup was closed before login completed
            }
          } finally {
            console.log('Setting up auth subscription after popup attempt');
            this.setupAuthSubscription();
          }
        }
      } else if (error.error === 'missing_refresh_token') {
        console.warn('Refresh token missing - will need re-authentication');
      } else {
        console.error('Silent authentication error:', error);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeBanner(): void {
    this.logoSrc.set(BANNER_CONFIG[this.banner()].logoUrl);
  }

  private setupAuthSubscription(): void {
    // Fallback if cookieCache is undefined
    this.auth
      .getUser()
      .then((user) => {
        console.log('Authenticated user:', user);
        this.handleUserUpdate(user);
      })
      .catch((error) => {
        console.error('Error fetching user:', error);
        this.handleAuthError();
      });
  }

  private handleUserUpdate(user: any): void {
    this.user.set(user);

    const ppc = user?.loyalty || '';
    // Update global user state
    couponUser.isLoggedIn = user ? true : false;
    couponUser.ppc = ppc;
    couponUser.name = user?.given_name || GUEST_USER;

    // Set API auth token
    this.couponAuth.setApiAuthToken(this.banner(), ppc);
  }

  private handleAuthError(): void {
    // Reset user state on error
    this.user.set(null);
    couponUser.isLoggedIn = false;
    couponUser.ppc = '';
    couponUser.name = GUEST_USER;
  }

  private buildLoginConfig(silent = false) {
    const currentBanner = this.banner();
    return {
      appState: {
        target: envs.useParamsForBanner
          ? window.location.pathname + window.location.search
          : window.location.search,
        banner: currentBanner,
      },
      authorizationParams: {
        redirect_uri: envs.useParamsForBanner
          ? `${window.location.origin}/${currentBanner}/callback`
          : `${window.location.origin}/callback`,
        scope:
          'mwg.ecm.storefrontui.apigateway:all openid profile email loyalty offline_access',
        response_type: 'code',
        prompt: silent ? ('none' as const) : ('login' as const),
        audience: process.env['AUTH0_AUDIENCE'] || '',
      },
    };
  }

  login(): void {
    if (!this.isInIframe()) {
      this.isLoading.set(true);

      try {
        // Build config without silent mode (prompt will be undefined)
        const loginConfig = this.buildLoginConfig(false);
        this.auth.loginWithRedirect(loginConfig);
      } catch (error) {
        console.error('Error during login:', error);
        this.isLoading.set(false);
      }
    } else {
      if (window.top) {
        const loginUrl = BANNER_CONFIG[this.banner()].xSiteHost;
        window.top.location.href = `${loginUrl}/loginredirect?RedirectUrl=${loginUrl}/socialoffer`;
      }
    }
  }

  logout(): void {
    this.isLoading.set(true);
    try {
      this.auth.logout({
        logoutParams: {
          returnTo: envs.useParamsForBanner
            ? window.location.origin + '/' + this.banner()
            : window.location.origin,
        },
      });
    } catch (error) {
      console.error('Error during logout:', error);
      this.isLoading.set(false);
    }
  }
}
